import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import AnimatedLogo from "@/components/AnimatedLogo";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { whatsappUrl } from "@/config/site.config";

const navLinkClass = ({ isActive }) =>
  `text-sm tracking-wide transition-colors duration-300 ${isActive ? "text-white" : "text-white/50 hover:text-white"}`;

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
          scrolled ? "glass border-b border-white/10" : "border-b border-transparent"
        }`}
        data-testid="main-navbar"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 md:px-12">
          <Link to="/" data-testid="nav-logo" aria-label="SNAPX TECHNOLOGIES LLP home">
            <AnimatedLogo />
          </Link>
          <nav className="hidden items-center gap-7 md:flex lg:gap-9">
            {NAV_LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} data-testid={link.testId} className={navLinkClass}>
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-start-project-button"
              className="group inline-flex items-center gap-1.5 rounded-full bg-volt px-5 py-2.5 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105"
            >
              Start a project
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a
              href={whatsappUrl()}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat with us on WhatsApp"
              title="Chat on WhatsApp"
              data-testid="nav-whatsapp-link"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/60 transition-colors duration-300 hover:border-[#25D366]/60 hover:bg-[#25D366]/10 hover:text-[#25D366]"
            >
              <WhatsAppIcon className="h-[18px] w-[18px]" />
            </a>
          </nav>
          <div className="flex items-center gap-2 md:hidden">
            <a
              href={whatsappUrl()}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat with us on WhatsApp"
              data-testid="nav-mobile-whatsapp-link"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors duration-300 active:border-[#25D366]/60 active:text-[#25D366]"
            >
              <WhatsAppIcon className="h-[18px] w-[18px]" />
            </a>
            <button
              className="flex h-10 w-10 items-center justify-center text-white"
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-menu-button"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink/95 px-8 backdrop-blur-xl md:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex flex-col gap-6">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                >
                  <Link to={link.to} className="font-serif-display text-4xl font-light text-white sm:text-5xl" data-testid={`nav-mobile-${link.label.toLowerCase()}-link`}>
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.44, duration: 0.5 }}>
                <Link to="/pricing" className="font-serif-display text-4xl font-light text-white sm:text-5xl" data-testid="nav-mobile-pricing-link">
                  Pricing
                </Link>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.5 }}>
                <Link
                  to="/contact"
                  data-testid="nav-mobile-start-project-button"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-volt px-7 py-3.5 text-base font-bold text-ink"
                >
                  Start a project <ArrowUpRight className="h-5 w-5" />
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
